import { Phone, MapPin, Clock, MessageCircle, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import BookingForm from "@/components/BookingForm";
import { Reveal } from "@/components/Reveal";
import { CLINIC } from "@/lib/site";

export default function BookAppointment() {
  return (
    <div data-testid="book-appointment-page" className="pt-[72px]">
      <Seo
        title="Book an Appointment | Kamana Physiotherapy, Gomti Nagar, Lucknow"
        description="Book your physiotherapy appointment at Kamana Physiotherapy & Rehab Center, Gomti Nagar, Lucknow. Open Mon–Sat 9 AM–9 PM, Sunday 9 AM–1 PM. Call +91 92508 23508."
      />

      <section className="bg-teal-obsidian text-alabaster grain-dark relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <p className="mono-label text-gold">Book an Appointment</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-4 leading-[1.06] max-w-3xl">
            Your recovery starts with <span className="italic text-gold">one appointment.</span>
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <BookingForm />
        </div>

        <aside className="lg:col-span-5 space-y-5">
          <Reveal delay={0.1}>
            <div className="bg-teal-brand text-alabaster rounded-3xl p-8">
              <p className="mono-label text-gold">Visit the Clinic</p>
              <ul className="mt-6 space-y-5 text-sm">
                <li className="flex gap-3.5">
                  <MapPin size={18} className="shrink-0 text-gold mt-0.5" />
                  <a href={CLINIC.mapsUrl} target="_blank" rel="noreferrer" data-testid="booking-address-link" className="leading-relaxed hover:text-gold transition-colors">
                    {CLINIC.address}
                  </a>
                </li>
                <li className="flex gap-3.5 items-center">
                  <Phone size={18} className="shrink-0 text-gold" />
                  <a href={CLINIC.phoneHref} data-testid="booking-phone-link" className="hover:text-gold transition-colors">
                    {CLINIC.phone}
                  </a>
                </li>
                <li className="flex gap-3.5">
                  <Clock size={18} className="shrink-0 text-gold mt-0.5" />
                  <span className="leading-relaxed">
                    {CLINIC.hours.map((h) => (
                      <span key={h.d} className="block">
                        {h.d}: {h.t}
                      </span>
                    ))}
                  </span>
                </li>
              </ul>
              <a
                href={CLINIC.whatsapp}
                target="_blank"
                rel="noreferrer"
                data-testid="booking-whatsapp-cta"
                className="mt-7 flex items-center justify-center gap-2 bg-gold text-teal-obsidian font-semibold text-sm rounded-full py-3.5 hover:bg-alabaster transition-colors"
              >
                <MessageCircle size={16} /> Or Message Us on WhatsApp
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="bg-gold-soft border border-gold/25 rounded-3xl p-8">
              <ShieldCheck className="text-gold-deep" size={22} />
              <p className="font-display text-xl font-semibold text-teal-brand mt-3">What to Expect</p>
              <ul className="mt-4 space-y-2.5 text-sm text-inkmuted">
                <li className="flex gap-2.5"><span className="text-gold-deep font-bold">1.</span> We call to confirm your slot within working hours.</li>
                <li className="flex gap-2.5"><span className="text-gold-deep font-bold">2.</span> A 45-minute clinical assessment maps your pain.</li>
                <li className="flex gap-2.5"><span className="text-gold-deep font-bold">3.</span> Your first relief session begins the same day.</li>
              </ul>
            </div>
          </Reveal>
        </aside>
      </section>
    </div>
  );
}
